import React, { createContext, useEffect, useMemo, useState } from "react";
import {
  fetchArbMonitor,
  fetchBondMonitor,
  fetchConvertNukes,
  fetchLatestAlerts,
  fetchPositions,
  fetchPreviousDayPositions,
  fetchSecurityInfo,
  fetchMissingTickers,
  fetchStratgyRegions,
  fetchStratgyAllowedRegions,
} from "../services/Axios";
import { useAuth } from "./AuthContext";
import { isFailedRefresh } from "./refreshRules";

// Initial datasets
const initialDatasets = {
  positions: [],
  previousDay: [],
  arbMonitorData: [],
  bondMonitorData: [],
  opsData: [],
  nukesData: [],
  securitiesData: [],
  missingTickersData: [],
  strategyRegionsData: [],
  strategyAllowedRegionsData: [],
};

// Define fetch intervals for each dataset (in milliseconds)
const fetchIntervals = {
  positions: 30000, // 30 seconds
  previousDay: 86400000, // 1 day
  arbMonitorData: 600000, // 600 seconds
  bondMonitorData: 600000, // 600 seconds
  opsData: 600000, // 600 seconds
  nukesData: 50000, // 50 seconds
  securitiesData: 3600000, // 3600 seconds
  missingTickersData: 60000, // 60 seconds
  strategyRegionsData: 600000, // 600 seconds
  strategyAllowedRegionsData: 86400000, // 1 day
};

export const PositionsDataContext = createContext();

export const PositionsDataProvider = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [datasets, setDatasets] = useState(initialDatasets);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedDatasets, setLoadedDatasets] = useState(new Set());

  // Generic fetch function for a single dataset
  const fetchData = async (fetchFn, key) => {
    try {
      const response = await fetchFn();
      setDatasets((prev) => {
        // A failed refresh must not blank screens that already have data:
        // keep what we have until the next poll succeeds.
        return isFailedRefresh(key, response, prev[key])
          ? prev
          : { ...prev, [key]: response };
      });
      setLoadedDatasets((prev) => new Set([...prev, key]));
      setError(null);
    } catch (err) {
      setError(`Failed to fetch ${key}`);
      console.error(`Error fetching ${key}:`, err);
      // Still mark as loaded to prevent infinite loading on error
      setLoadedDatasets((prev) => new Set([...prev, key]));
    }
  };

  // Check if all datasets are loaded
  const allDatasetsLoaded = useMemo(() => {
    const requiredDatasets = Object.keys(initialDatasets);
    return requiredDatasets.every((dataset) => loadedDatasets.has(dataset));
  }, [loadedDatasets]);

  // Update loading state when all datasets are loaded
  useEffect(() => {
    if (allDatasetsLoaded) {
      setIsLoading(false);
    }
  }, [allDatasetsLoaded]);

  // Set up individual useEffect hooks for each dataset
  useEffect(() => {
    // Don't fire any request until auth is actually ready - otherwise these
    // race ahead of token acquisition on first landing and get 403s.
    if (!isAuthenticated || authLoading) return;

    const fetchFunctions = {
      positions: fetchPositions,
      previousDay: fetchPreviousDayPositions,
      arbMonitorData: fetchArbMonitor,
      bondMonitorData: fetchBondMonitor,
      opsData: fetchLatestAlerts,
      nukesData: fetchConvertNukes,
      securitiesData: fetchSecurityInfo,
      missingTickersData: fetchMissingTickers,
      strategyRegionsData: fetchStratgyRegions,
      strategyAllowedRegionsData: fetchStratgyAllowedRegions,
    };

    // Initial fetch for all datasets
    Object.entries(fetchFunctions).forEach(([key, fetchFn]) => {
      fetchData(fetchFn, key);
    });

    // Set up intervals for each dataset
    const intervals = Object.entries(fetchFunctions).map(([key, fetchFn]) => {
      const interval = setInterval(() => {
        fetchData(fetchFn, key);
      }, fetchIntervals[key]);
      return interval;
    });

    // Cleanup all intervals on component unmount
    return () => intervals.forEach((interval) => clearInterval(interval));
  }, [isAuthenticated, authLoading]);

  // Function to manually update datasets
  const updateDatasets = (newDatasets) => {
    setDatasets((prev) => ({ ...prev, ...newDatasets }));
    setError(null);
  };

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({ datasets, updateDatasets, error, isLoading }),
    [datasets, error, isLoading]
  );

  return (
    <PositionsDataContext.Provider value={value}>
      {children}
    </PositionsDataContext.Provider>
  );
};
